/**
 * WaveTrendChart - WaveTrend oscillator with WT1/WT2 lines
 */

import { useEffect, useRef, useState } from 'react';
import { createChart } from 'lightweight-charts';
import './WaveTrendChart.css';

export default function WaveTrendChart({ data, height = 150, onVisibleRangeChange }) {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const wt1SeriesRef = useRef(null);
  const [wtValues, setWtValues] = useState(null);

  useEffect(() => {
    if (!chartContainerRef.current || !data || !data.wavetrend) return;

    // Early validation: check if wt1 data exists before proceeding
    if (!data.wavetrend.wt1 || data.wavetrend.wt1.length === 0) {
      console.log('[WaveTrendChart] No wt1 data available');
      return;
    }

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height,
      layout: {
        background: { color: '#1a1a1a' },
        textColor: '#d1d4dc',
      },
      grid: {
        vertLines: { color: '#2b2b2b' },
        horzLines: { color: '#2b2b2b' },
      },
      crosshair: {
        mode: 1,
      },
      timeScale: {
        borderColor: '#2b2b2b',
        visible: true,
      },
      leftPriceScale: {
        borderColor: '#2b2b2b',
        autoScale: true,
        visible: true,
        scaleMargins: {
          top: 0.1,
          bottom: 0.1,
        },
      },
      rightPriceScale: {
        visible: false,
      },
    });

    chartRef.current = chart;

    // Add overbought/oversold zones as background
    const addZoneLine = (value, color) => {
      const zoneSeries = chart.addLineSeries({
        priceScaleId: 'left',
        color,
        lineWidth: 1,
        lineStyle: 2, // Dashed
      });

      const zoneData = data.wavetrend.wt1.map(d => ({
        time: d.time,
        value: value,
      }));

      zoneSeries.setData(zoneData);
    };

    // Add zone lines (now safe since we validated wt1 exists)
    addZoneLine(60, '#f44336'); // Overbought
    addZoneLine(-60, '#4caf50'); // Oversold
    addZoneLine(0, '#757575'); // Zero line

    // Add WT1 line
    const wt1Series = chart.addLineSeries({
      priceScaleId: 'left',
      color: '#00bcd4',
      lineWidth: 3,
      title: 'WT1',
    });
    wt1SeriesRef.current = wt1Series;

    const wt1Data = data.wavetrend.wt1.map(d => ({
      time: d.time,
      value: d.value,
    }));

    wt1Series.setData(wt1Data);

    // Add WT2 line
    if (data.wavetrend.wt2 && data.wavetrend.wt2.length > 0) {
      const wt2Series = chart.addLineSeries({
        priceScaleId: 'left',
        color: '#ff9800',
        lineWidth: 3,
        title: 'WT2',
      });

      const wt2Data = data.wavetrend.wt2.map(d => ({
        time: d.time,
        value: d.value,
      }));

      wt2Series.setData(wt2Data);
    }

    // Subscribe to crosshair movement for value display
    chart.subscribeCrosshairMove((param) => {
      if (param.time && param.seriesData && param.seriesData.size > 0) {
        let wt1Val = null, wt2Val = null;
        param.seriesData.forEach((data, series) => {
          if (data && typeof data.value !== 'undefined') {
            const seriesOptions = series.options();
            if (seriesOptions.color === '#00bcd4') {
              wt1Val = data.value;
            } else if (seriesOptions.color === '#ff9800') {
              wt2Val = data.value;
            }
          }
        });
        setWtValues({ wt1: wt1Val, wt2: wt2Val });
      } else {
        if (data.wavetrend.wt1 && data.wavetrend.wt1.length > 0) {
          const latest = data.wavetrend.wt1.length - 1;
          setWtValues({
            wt1: data.wavetrend.wt1[latest]?.value,
            wt2: data.wavetrend.wt2?.[latest]?.value
          });
        }
      }

      // Sync crosshair across all charts
      if (window.chartSync && chart) {
        window.chartSync.syncCrosshair(chart, param);
      }
    });

    // Initialize with latest values
    if (data.wavetrend.wt1 && data.wavetrend.wt1.length > 0) {
      const latest = data.wavetrend.wt1.length - 1;
      setWtValues({
        wt1: data.wavetrend.wt1[latest]?.value,
        wt2: data.wavetrend.wt2?.[latest]?.value
      });
    }

    if (onVisibleRangeChange) {
      chart.timeScale().subscribeVisibleTimeRangeChange(onVisibleRangeChange);
    }

    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({
          width: chartContainerRef.current.clientWidth,
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, [data, height, onVisibleRangeChange]);

  useEffect(() => {
    if (chartRef.current && wt1SeriesRef.current && window.chartSync) {
      window.chartSync.registerChart('wavetrend', chartRef.current, wt1SeriesRef.current);
    }
  }, [chartRef.current, wt1SeriesRef.current]);

  return (
    <div className="wavetrend-chart">
      <div className="chart-value-display">
        {wtValues && (
          <span className="wt-values">
            WT1: {wtValues.wt1 ? parseFloat(wtValues.wt1).toFixed(4) : null ?? 'N/A'} WT2: {wtValues.wt2 ? parseFloat(wtValues.wt2).toFixed(4) : null ?? 'N/A'}
          </span>
        )}
      </div>
      <div ref={chartContainerRef} className="chart-container" />
    </div>
  );
}
